import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { HouseholdOriginSource } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { LeftAlignedButton } from './button.tsx';
import { ButtonContent, goToNextOnboardingStep, Message, setOnboardingHouseholdSurveyResponse } from './common.tsx';
import { Frame } from './frame.tsx';

interface Option {
  icon: React.ReactElement;
  prompt: string;
  source: HouseholdOriginSource;
}

export const SurveyDiscovery = () => {
  const options: Option[] = [
    {
      icon: <Icon key="cross" src="/images/cross.png" />,
      prompt: 'From my church',
      source: HouseholdOriginSource.Church,
    },
    {
      icon: <Icon key="pencil" src="/images/pencil.png" />,
      prompt: 'From a school',
      source: HouseholdOriginSource.School,
    },
    {
      icon: <Icon key="friend" src="/images/friend.png" />,
      prompt: 'From a friend',
      source: HouseholdOriginSource.Friend,
    },
    {
      icon: <Icon key="self" src="/images/magnifying-glass.png" />,
      prompt: 'Found it myself',
      source: HouseholdOriginSource.Self,
    },
  ];

  return (
    <Frame
      onboardingPage={OnboardingPages.SurveyDiscovery}
      mainContent={
        <>
          <Message $widthInChars={22}>How did you hear about us?</Message>
          <ButtonContent>
            {options.map((option) => (
              <SourceButton key={option.source} {...option} />
            ))}
          </ButtonContent>
        </>
      }
    />
  );
};

function SourceButton({ icon, prompt, source }: Option) {
  const navigate = useNavigate();
  const { origin: selectedSource } = useLocalStorage(LocalStorageKey.OnboardingHouseholdSurveyResponses)[0];

  const onSetSource = useCallback(async () => {
    setOnboardingHouseholdSurveyResponse({ origin: source });
    await goToNextOnboardingStep(navigate, { from: OnboardingPages.SurveyDiscovery, action: 'originChosen' });
  }, [navigate, source]);

  return (
    <LeftAlignedButton onClick={onSetSource} notPrimary={true} selected={source === selectedSource}>
      {icon}
      {prompt}
    </LeftAlignedButton>
  );
}

const Icon = styled.img`
  width: 55px;
  height: 55px;
  margin: 10px 10px 10px 20px;
`;
